import { supabase } from '../lib/supabase';

// Error handling helper
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }
  
  const camelObj = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = toCamelCase(obj?.[key]);
  });
  return camelObj;
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  }
  
  const snakeObj = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    snakeObj[snakeKey] = toSnakeCase(obj?.[key]);
  });
  return snakeObj;
}

// Client Portal Service
export const clientPortalService = {
  // Fetch client profile
  async getClientProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return null;
        } else {
          console.log('Profile fetch error:', error?.message);
          return null;
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching client profile:', error?.message);
      throw error;
    }
  },

  // Fetch active cases for client
  async getActiveCases(clientId) {
    try {
      const { data, error } = await supabase?.from('pro_bono_cases')?.select(`
          *,
          assigned_lawyer:user_profiles!pro_bono_cases_assigned_lawyer_id_fkey(full_name)
        `)?.eq('client_id', clientId)?.in('assignment_status', ['assigned', 'in_progress'])?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Cases fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching active cases:', error?.message);
      throw error;
    }
  },

  // Fetch documents for client
  async getDocuments(clientId, caseId = null) {
    try {
      let query = supabase?.from('documents')?.select(`
          *,
          uploaded_by_user:user_profiles!documents_uploaded_by_fkey(full_name)
        `)?.eq('client_id', clientId)?.order('upload_date', { ascending: false });

      if (caseId) {
        query = query?.eq('case_id', caseId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Documents fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error?.message);
      throw error;
    }
  },

  // Fetch conversations for client
  async getConversations(userId) {
    try {
      const { data, error } = await supabase?.from('conversations')?.select(`
          *,
          lawyer:user_profiles!conversations_lawyer_id_fkey(full_name, avatar_url),
          case:pro_bono_cases(id, case_title)
        `)?.or(`client_id.eq.${userId},lawyer_id.eq.${userId}`)?.order('last_message_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Conversations fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error?.message);
      throw error;
    }
  },

  // Fetch messages for a conversation
  async getMessages(conversationId) {
    try {
      const { data, error } = await supabase?.from('messages')?.select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(full_name, avatar_url)
        `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Messages fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error?.message);
      throw error;
    }
  },

  // Fetch consultations for client
  async getConsultations(userId) {
    try {
      const { data, error } = await supabase?.from('consultations')?.select(`
          *,
          lawyer:user_profiles!consultations_lawyer_id_fkey(full_name, avatar_url),
          case:pro_bono_cases(case_title)
        `)?.or(`client_id.eq.${userId},lawyer_id.eq.${userId}`)?.order('consultation_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Consultations fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching consultations:', error?.message);
      throw error;
    }
  },

  // Fetch invoices for client
  async getInvoices(clientId) {
    try {
      const { data, error } = await supabase?.from('invoices')?.select(`
          *,
          lawyer:user_profiles!invoices_lawyer_id_fkey(full_name)
        `)?.eq('client_id', clientId)?.order('issue_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Invoices fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error?.message);
      throw error;
    }
  },

  // Fetch case timeline events
  async getCaseTimelineEvents(caseId) {
    try {
      const { data, error } = await supabase?.from('case_timeline_events')?.select(`
          *,
          lawyer:user_profiles!case_timeline_events_lawyer_id_fkey(full_name)
        `)?.eq('case_id', caseId)?.order('event_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Timeline events fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching timeline events:', error?.message);
      throw error;
    }
  },

  // Fetch notifications for user
  async getNotifications(userId, limit = 10) {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Notifications fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error?.message);
      throw error;
    }
  },

  // Fetch lawyer matches for client
  async getLawyerMatches(clientId) {
    try {
      const { data, error } = await supabase?.from('lawyer_matches')?.select(`
          *,
          lawyer:user_profiles!lawyer_matches_lawyer_id_fkey(full_name, avatar_url, organization)
        `)?.eq('client_id', clientId)?.order('match_score', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Lawyer matches fetch error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching lawyer matches:', error?.message);
      throw error;
    }
  },

  // Subscribe to real-time updates
  subscribeToUpdates(userId, callbacks) {
    const channels = [];

    // Subscribe to cases updates
    if (callbacks?.onCaseUpdate) {
      const casesChannel = supabase?.channel('cases_changes')?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'pro_bono_cases',
            filter: `client_id=eq.${userId}`
          },
          (payload) => {
            callbacks?.onCaseUpdate(toCamelCase(payload));
          }
        )?.subscribe();
      channels?.push(casesChannel);
    }

    // Subscribe to documents updates
    if (callbacks?.onDocumentUpdate) {
      const documentsChannel = supabase?.channel('documents_changes')?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'documents',
            filter: `client_id=eq.${userId}`
          },
          (payload) => {
            callbacks?.onDocumentUpdate(toCamelCase(payload));
          }
        )?.subscribe();
      channels?.push(documentsChannel);
    }

    // Subscribe to messages updates
    if (callbacks?.onMessageUpdate) {
      const messagesChannel = supabase?.channel('messages_changes')?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          (payload) => {
            callbacks?.onMessageUpdate(toCamelCase(payload));
          }
        )?.subscribe();
      channels?.push(messagesChannel);
    }

    // Subscribe to notifications updates
    if (callbacks?.onNotificationUpdate) {
      const notificationsChannel = supabase?.channel('notifications_changes')?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            callbacks?.onNotificationUpdate(toCamelCase(payload));
          }
        )?.subscribe();
      channels?.push(notificationsChannel);
    }

    // Return cleanup function
    return () => {
      channels?.forEach(channel => {
        supabase?.removeChannel(channel);
      });
    };
  }
};

export default clientPortalService;